import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  OnDestroy,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { BenefitPeriod } from "certification/models/cti";
import { CertificationService } from "certification/services/certification-service";
import { CareLocationTypeComponent } from "components/order-management/care-location-type/care-location-type";
import { CareLocationComponent } from "components/order-management/care-location/care-location";
import { VerificationComponent } from "components/order-management/checkbox/checkbox";
import { CurrentDiagnosisComponent } from "components/order-management/current-diagnosis/current-diagnosis";
import { DatePickerComponent } from "components/order-management/date-picker/date-picker";
import { OrderingSearchInputComponent } from "components/order-management/diagnosis-search/diagnosis-search";
import { DisciplineComponent } from "components/order-management/discipline/discipline";
import { OrderingDropdownComponent } from "components/order-management/dropdown/dropdown";
import { FileUploadComponent } from "components/order-management/file-upload/file-upload";
import { LevelOfCareComponent } from "components/order-management/level-of-care/level-of-care";
import { MARDetailsComponent } from "components/order-management/mar-details/mar-details";
import { MedicationIngredientsScriptsureComponent } from "components/order-management/medication-ingredients-scriptsure/medication-ingredients-scriptsure";
import { MedicationNameDescriptionScriptSureComponent } from "components/order-management/medication-name-description-scriptsure/medication-name-description-scriptsure";
import { MedicationStrengthSelectorScripsureComponent } from "components/order-management/medication-strength-selector-scriptsure/medication-strength-selector-scriptsure";
import { MultiOptionComponent } from "components/order-management/multi-option/multi-option";
import {
  OrderComponentInterface,
  OrderCreateRequestUpdate,
} from "components/order-management/order-components-interface";
import { OrderingProviderComponent } from "components/order-management/ordering-provider/ordering-provider";
import { Quantity } from "components/order-management/quantity/quantity";
import { RadioGroupComponent } from "components/order-management/radio-group/radio-group";
import { ReasonsForGIPComponent } from "components/order-management/reasons-for-gip/reasons-for-gip";
import { ProviderNotesComponent } from "components/order-management/text-area/text-area";
import { OrderTextFieldComponent } from "components/order-management/text-field/text-field";
import { VisitFrequencyDescription } from "components/order-management/visit-frequency-description/visit-frequency-description";
import {
  Events,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import {
  DropdownItem,
  Order,
  OrderComponent,
  OrderComponentProperty,
  OrderResponse,
  OrderTypeConfiguration,
  OrderVisitFrequency,
} from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import CredentialedResource from "models/resource-management/credentialed-resource";
import moment from "moment";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { OrderComponentPropertyUpdate } from "../../../components/order-management/order-components-interface";
import { PatientLocationComponent } from "../../../components/order-management/patient-location/patient-location";
import { ReasonForRespiteComponent } from "../../../components/order-management/reason-for-respite/reason-for-respite";
import { AmendedAction } from "../../../models/order-management/amendedAction";
import { CalendarService } from "../../../providers/calendar-service/calendar-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { NotImplemented } from "./not-implemented.component";
import { OrderFormState } from "./order-form-state";

export interface PrefilledData {
  orderType?: string;
  patientChartId?: number;
  nameDescription?: string;
  route?: string;
  dosage?: string;
  frequency?: string;
  strength?: string;
  symptoms?: string;
  isFreeText?: boolean;
  MED_ROUTED_MED_ID?: number;
}

@Component({
  selector: "add-order",
  templateUrl: "add-order.html",
})
export class AddOrder implements OnDestroy {
  title: string = "addOrder";
  titleHeader: string = "";
  orderTypeConfigurations: OrderTypeConfiguration[] = [];
  orderForm: FormGroup;
  orderTypeDropdownItems: DropdownItem[] = [];
  orderCreateRequest: Partial<Order> = {};
  orderAmendedRequest: Partial<AmendedAction> = {
    properties: [],
  };
  orderFormState: OrderFormState;
  invalidComponents: number[] = [];
  orders: Array<StatefulOrderResponse> = [];
  locOrders: Array<StatefulOrderResponse> = [];
  vfOrders: Array<StatefulOrderResponse> = [];
  order: OrderResponse;
  formType: string;
  credentialedResource: CredentialedResource;
  patientChartId: number;
  isProfesionalProvider: boolean;
  prefilledData: PrefilledData = null;
  shouldCancelButtonBeDisabled: boolean = false;
  patientExternalId: number;
  private benefitPeriodSub: Subscription;
  private destroy$ = new Subject<void>();

  get isFormInvalid() {
    return (
      this.invalidComponents.length > 0 || !this.orderCreateRequest.orderType
    );
  }

  @ViewChild("orderComponents", { read: ViewContainerRef }) orderComponents;
  componentReferences: ComponentRef<OrderComponentInterface>[] = [];

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public events: Events,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    private resolver: ComponentFactoryResolver,
    private renderer2: Renderer2,
    private certificationService: CertificationService,
    private calendarService: CalendarService
  ) {
    this.orderTypeConfigurations = this.navParams.data.orderTypeConfigurations;
    this.locOrders = this.navParams.data.locOrders;
    this.vfOrders = this.navParams.data.vfOrders;
    this.patientChartId = this.navParams.data.patientChartId;
    this.patientExternalId = this.navParams.data.patientId;
    this.order =
      this.navParams.data.formType === "convertToOrder" ||
      "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare"
        ? this.navParams.data.prefilledData
        : this.navParams.data.order;
    this.formType = this.navParams.data.formType;
    this.initOrderFormState();
    const titleMap = {
      amendment: "amendOrder",
      convertToOrder: "convertToOrder",
      add: "addOrder",
    };

    this.title = titleMap[this.formType] || titleMap.add;
    this.events.subscribe("sendForm", () => {
      this.sendForm();
    });
    this.prefilledData = this.navParams.data.prefilledData;
    this.shouldCancelButtonBeDisabled =
      this.formType ===
      "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare";
  }

  ngOnDestroy() {
    if (this.benefitPeriodSub) {
      this.benefitPeriodSub.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  getVFOrderTitleHeader(): string {
    let bpEndDate = "Not Available";

    if (
      this.orderFormState &&
      this.orderFormState.benefitPeriodCertificationEndDate
    ) {
      const benefitPeriodMoment = moment(
        this.orderFormState.benefitPeriodCertificationEndDate
      );
      if (benefitPeriodMoment.isValid()) {
        bpEndDate = `${benefitPeriodMoment.format("MM/DD/YYYY")}`;
      }
    }

    const todayDay = new Date().getDay();
    const remainingDays: number = 6 - todayDay + 1;

    return `- BP ends: ${bpEndDate} (${remainingDays} day${
      remainingDays === 1 ? "" : "s"
    } left in this Medicare Week)`;
  }

  orderCreateRequest$ = new Subject<OrderCreateRequestUpdate>();

  initOrderFormState() {
    this.orderFormState = {
      locOrders: this.locOrders,
      vfOrders: this.vfOrders,
      patientChartId: this.navParams.data.patientChartId,
      credentialedResource: this.navParams.data.credentialedResource,
      isEPrescribeEnabled: this.navParams.data.isEPrescribeEnabled,
      benefitPeriodCertificationEndDate:
        this.navParams.data.benefitPeriodCertificationEndDate,
      latestCompletedVisitByOrderId: {},
    };
    this.prefetchLatestCompletedVisits();
  }

  /**
   * NOVA-1439: pre-fetch the latest COMPLETED visit (by startDatetime) for every
   * existing VF order so the date-picker can decide, in memory, whether a
   * backdated new start_date will strand completed visits on the order being
   * auto-discontinued. Fire-and-forget — if a request fails or is slow, the
   * compliance warning simply won't display for that order; the existing
   * overlap warning is unaffected.
   */
  private prefetchLatestCompletedVisits(): void {
    if (!this.vfOrders || this.vfOrders.length === 0) {
      return;
    }
    for (const order of this.vfOrders) {
      if (!order.orderId) {
        continue;
      }
      // scheduling-open-search default sort for EVENT is startDatetime ASC and
      // does not accept "startDatetime" via sortBy. Pull a generous slice of
      // COMPLETED visits for this order and compute the latest client-side.
      const request = {
        dataType: "EVENT",
        mustFields: [{ name: "orderId", value: order.orderId }],
        optionalFields: [{ name: "status", value: "COMPLETED" }],
        from: 0,
        size: 500,
      };
      this.calendarService
        .getSchedulingEvents(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: any) => {
            const hits: any[] = (response && response["hits"]) || [];
            let latest = "";
            for (const hit of hits) {
              const startDatetime = hit && hit.startDatetime;
              if (startDatetime && startDatetime > latest) {
                latest = startDatetime;
              }
            }
            if (!latest) {
              return;
            }
            this.orderFormState = {
              ...this.orderFormState,
              latestCompletedVisitByOrderId: {
                ...(this.orderFormState.latestCompletedVisitByOrderId || {}),
                [order.orderId]: latest,
              },
            };
            this.propagateOrderFormState();
          },
          () => {
            // Best-effort: swallow so the form stays usable if scheduling search fails.
          }
        );
    }
  }

  onAmendedOrderCreateRequestChange($event: OrderComponentPropertyUpdate) {
    if ($event.updates.propertyName === "reason") {
      this.orderAmendedRequest.reason = $event.updates.newValue as string;
    } else {
      // Create a map to store unique updates based on propertyName
      const uniqueUpdatesMap = new Map<string, OrderComponentProperty>();

      // Add existing updates to the uniqueUpdatesMap
      this.orderAmendedRequest.properties.forEach((update) => {
        if (update.propertyName) {
          uniqueUpdatesMap.set(update.propertyName, update);
        }
      });

      // Check for duplicates and add new updates to the uniqueUpdatesMap
      if ($event.updates.propertyName) {
        uniqueUpdatesMap.set($event.updates.propertyName, $event.updates);
      }

      // Update the properties with the unique updates
      this.orderAmendedRequest.properties = Array.from(
        uniqueUpdatesMap.values()
      );
    }
    if ($event.deletes) {
      for (const property of $event.deletes) {
        this.orderAmendedRequest.properties =
          this.orderAmendedRequest.properties.filter(
            (obj) => obj.propertyName !== property
          );
      }
    }
  }

  onOrderCreateRequestChange(event: OrderCreateRequestUpdate) {
    this.orderCreateRequest = {
      ...this.orderCreateRequest,
      ...event.updates,
    };
    if (event.deletes) {
      for (const property of event.deletes) {
        delete this.orderCreateRequest[property];
      }
    }

    // Keep: this is very useful for debugging
    console.debug(this.orderCreateRequest);

    //Dynamic components require manual change detection
    for (const componentRef of this.componentReferences) {
      componentRef.instance.orderCreateRequest = this
        .orderCreateRequest as Order;
      componentRef.changeDetectorRef.detectChanges();
    }
    this.orderCreateRequest$.next(event);
  }

  onOrderFormStateEvent(event: OrderFormState) {
    const prevState = { ...this.orderFormState };
    this.orderFormState = {
      ...this.orderFormState,
      ...event,
    };
    this.propagateOrderFormState();
    this.updateBenefitPeriodForPRN(prevState);
  }

  private propagateOrderFormState() {
    for (const componentRef of this.componentReferences) {
      componentRef.instance.orderFormState = this.orderFormState;
      componentRef.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Queries the benefit period for the current Start Date when the order is PRN-only.
   * Triggers when: PRN-only state activates or Start Date changes.
   * Clears the benefit period default when Start Date is removed.
   */
  private updateBenefitPeriodForPRN(prevState: OrderFormState) {
    if (this.orderCreateRequest.orderType !== "VISIT_FREQUENCY") {
      return;
    }
    if (!OrderVisitFrequency.isPRNOnlyOrder(this.orderFormState)) {
      return;
    }

    const startDate = this.orderFormState.startDate;
    if (!startDate) {
      if (this.orderFormState.benefitPeriodCertificationEndDate) {
        this.orderFormState = {
          ...this.orderFormState,
          benefitPeriodCertificationEndDate: undefined,
        };
        this.propagateOrderFormState();
      }
      return;
    }

    const wasPRNOnly = OrderVisitFrequency.isPRNOnlyOrder(prevState);
    const startDateChanged =
      !prevState.startDate ||
      startDate.getTime() !== prevState.startDate.getTime();
    const justBecamePRNOnly = !wasPRNOnly;

    // Only re-query when the PRN-only state just activated or the Start Date changed
    if (!justBecamePRNOnly && !startDateChanged) {
      return;
    }

    if (this.benefitPeriodSub) {
      this.benefitPeriodSub.unsubscribe();
    }

    const dateStr = moment(startDate).format("YYYY-MM-DD");
    this.benefitPeriodSub = this.certificationService
      .GetCurrentBenefitPeriod(this.patientExternalId, dateStr)
      .subscribe({
        next: (benefitPeriod: BenefitPeriod) => {
          const endDate = benefitPeriod?.endDate
            ? moment(benefitPeriod.endDate).toDate()
            : undefined;
          this.orderFormState = {
            ...this.orderFormState,
            benefitPeriodCertificationEndDate: endDate,
          };
          this.propagateOrderFormState();
        },
        error: () => {
          // API failure — clear the benefit period default so the user must enter manually
          this.orderFormState = {
            ...this.orderFormState,
            benefitPeriodCertificationEndDate: undefined,
          };
          this.propagateOrderFormState();
        },
      });
  }

  ngOnInit() {
    this.isProfesionalProvider = isProfessionalProvider(
      this.orderFormState.credentialedResource
    );
    this.orderForm = new FormGroup({});
    if (
      this.formType === "amendment" ||
      this.formType === "convertToOrder" ||
      this.formType ===
        "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare"
    )
      this.loadComponentsAmended();
    this.orderCreateRequest$.subscribe((event) =>
      this.onOrderTypeChanged(event)
    );
  }

  loadComponentsAmended() {
    this.orderCreateRequest = { orderType: this.order.orderType };
    this.loadOrderComponents(this.order.orderType);
  }

  onOrderTypeChanged(event: OrderCreateRequestUpdate) {
    if (event.updates && event.updates.orderType == "MEDICATION|ERX") {
      this.viewCtrl.dismiss({
        showEprescribeIframe: true,
      });
    }
    if (event.updates && event.updates.orderType) {
      this.loadOrderComponents(event.updates.orderType);
    }
  }

  onSubmit(_event: any) {
    this.events.publish("fileUpload");
  }

  sendForm() {
    if (this.formType === "amendment") {
      this.viewCtrl.dismiss(this.orderAmendedRequest);
    } else {
      this.viewCtrl.dismiss({
        orderCreateRequest: this.orderCreateRequest,
        orderFormState: this.orderFormState,
      });
    }
  }

  dismiss() {
    this.viewCtrl.dismiss({
      redirectMedication: this.formType === "convertToOrder",
    });
  }

  loadOrderComponents(orderType?: string) {
    const viewContainerRef: ViewContainerRef = this.orderComponents;
    viewContainerRef.clear();
    this.invalidComponents = [];
    this.componentReferences = [];
    this.orderCreateRequest = { orderType: orderType };
    this.initOrderFormState();
    if (
      this.formType ===
      "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare"
    ) {
      this.orderCreateRequest = {
        orderType: this.prefilledData.orderType,
      };
      this.orderFormState = {
        ...this.orderFormState,
      };
    }
    if (this.formType === "convertToOrder") {
      this.orderCreateRequest = {
        orderType: this.prefilledData.orderType,
        patientChartId: this.prefilledData.patientChartId,
        nameDescription: this.prefilledData.nameDescription,
        dosageRoute: this.prefilledData.route,
        dosageDosage: this.prefilledData.dosage,
        dosageFrequency: this.prefilledData.frequency,
        dosageStrength: this.prefilledData.strength,
        symptoms: this.prefilledData.symptoms,
        isCompound: this.prefilledData?.isFreeText ?? false,
      };
      this.orderFormState = {
        ...this.orderFormState,
        credentialedResource: this.navParams.data.credentialedResource,
        dosageParameters: {
          dosageParameters: [],
          dosage: [this.prefilledData.dosage],
          frequency: [this.prefilledData.frequency],
        },
        formType: "convertToOrder",
        medRouteId: this.prefilledData.MED_ROUTED_MED_ID,
        isCompound: this.prefilledData?.isFreeText ?? false,
      };
    }
    if (this.formType === "amendment") {
      this.orderCreateRequest = this.order;
      this.orderFormState = {
        ...this.orderFormState,
        dosageParameters: {
          dosageParameters: [],
          dosage: [this.order.dosageDosage],
          frequency: [this.order.dosageFrequency],
        },
        order: this.order,
        approvalType: this.order.approvalType,
        levelOfCare: this.order.levelOfCare,
        careLocationType: this.order.careLocationType,
        patientLocation: this.order.patientLocation,
        careLocation: this.order.careLocation,
        formType: this.formType,
        isCompound: this.order.isCompound,
      };
    }
    const orderTypeConfiguration = this.orderTypeConfigurations.find(
      (configuration) => configuration.orderType === orderType
    );
    if (orderTypeConfiguration) {
      let i = 0;
      this.titleHeader =
        orderType === "VISIT_FREQUENCY" ? this.getVFOrderTitleHeader() : "";
      for (const orderComponent of orderTypeConfiguration.components) {
        orderComponent.id = i;
        const classAndCallback = this.getClassAndCallback(orderComponent);
        const factory =
          this.resolver.resolveComponentFactory<OrderComponentInterface>(
            classAndCallback.className
          );
        const componentRef = viewContainerRef.createComponent(factory);
        componentRef.instance.orderComponent = orderComponent;
        componentRef.instance.orderCreateRequest = this
          .orderCreateRequest as Order;
        componentRef.instance.orderFormState = this.orderFormState;

        if (this.formType === "amendment") {
          componentRef.instance.amendedOrderRequestEvent.subscribe(
            ($event: OrderComponentPropertyUpdate) => {
              this.onAmendedOrderCreateRequestChange($event);
            }
          );
        }
        componentRef.instance.orderCreateRequestEvent.subscribe(
          ($event: OrderCreateRequestUpdate) => {
            this.onOrderCreateRequestChange($event);
          }
        );
        componentRef.instance.orderFormStateEvent.subscribe(
          ($event: OrderFormState) => {
            this.onOrderFormStateEvent($event);
          }
        );
        componentRef.instance.isValidEvent.subscribe(($event: boolean) =>
          this.setInvalidForms(orderComponent.id, $event)
        );
        // For extensibility
        if (classAndCallback.callback) {
          classAndCallback.callback(componentRef.instance);
        }
        componentRef.changeDetectorRef.detectChanges(); // kicks off ngOnInit()
        this.componentReferences = [...this.componentReferences, componentRef];
        i++;
        // Add class
        if (orderComponent.additionalClasses) {
          this.renderer2.addClass(
            componentRef.location.nativeElement,
            orderComponent.additionalClasses
          );
        }
      }
    }
  }

  setInvalidForms(id: number, isValid: boolean) {
    if (isValid) {
      this.invalidComponents = this.invalidComponents.filter(
        (form) => form != id
      );
    } else {
      this.invalidComponents = [...this.invalidComponents, id];
    }
  }

  getClassAndCallback(component: OrderComponent): ClassAndCallback {
    switch (component.componentName) {
      case "multi-option":
        return { className: MultiOptionComponent };
      case "ordering-provider":
        return { className: OrderingProviderComponent };
      case "date-picker":
        return { className: DatePickerComponent };
      case "text-area":
        return { className: ProviderNotesComponent };
      case "radio-group":
        return { className: RadioGroupComponent };
      case "checkbox":
        return { className: VerificationComponent };
      case "file-upload":
        return { className: FileUploadComponent };
      case "diagnosis-search":
        return { className: OrderingSearchInputComponent };
      case "current-diagnosis":
        return { className: CurrentDiagnosisComponent };
      case "medication-ingredients-scriptsure":
        return { className: MedicationIngredientsScriptsureComponent };
      case "text-field":
        return { className: OrderTextFieldComponent };
      case "level-of-care":
        return { className: LevelOfCareComponent };
      case "care-location-type":
        return { className: CareLocationTypeComponent };
      case "care-location":
        return { className: CareLocationComponent };
      case "dropdown":
        return { className: OrderingDropdownComponent };
      case "reason-for-respite":
        return { className: ReasonForRespiteComponent };
      case "patient-location":
        return { className: PatientLocationComponent };
      case "reasons-for-gip":
        return { className: ReasonsForGIPComponent };
      case "medication-strength-selector-scriptsure":
        return { className: MedicationStrengthSelectorScripsureComponent };
      case "medication-name-description-scriptsure":
        return { className: MedicationNameDescriptionScriptSureComponent };
      case "quantity":
        return { className: Quantity };
      case "visit-frequency-description":
        return { className: VisitFrequencyDescription };
      case "mar-details":
        return { className: MARDetailsComponent };
      case "discipline":
        return { className: DisciplineComponent };
      default:
        break;
    }

    return { className: NotImplemented };
  }
}
interface ClassAndCallback {
  className: any;
  callback?(orderComponent: OrderComponentInterface): void;
}



// WEBPACK FOOTER //
// ./src/pages/orders-entry-om/add-order/add-order.ts